import {
  AllStructuresForHomePageQuery,
  useAllStructuresForHomePageQuery,
  useSyncAnalyticsDataMutation,
} from '@/__generated__/graphql';
import Grid from '@mui/material/Grid2';
import { useFeatureFlag, useRouter, useHavePermission } from '@/hooks';
import { Permissions } from '@/types';
import * as state from '@/state/atoms';
import { abyssColors } from '@/theme/colors';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Stack, Tabs, Typography, Tab } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { XlsxProcessor } from '@/utils/xlsx';
import { PageLoader } from '../shared/PageLoader';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { snackbarMessage } from '@/state/atoms';
import { DeploymentCard } from './DeploymentCard';
import { PaintBockAndDeckGraphsContainer } from './PaintBlockAndDeckGraphsContainer/PaintBlockAndDeckGraphsCotainer';
import { AssetsContainer, MainContainer } from './styles';
import { TabContent } from '../Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent';
import { TemporalAnalysisGraphsContainer } from './TemporalAnalysisGraphsContainer/TemporalAnalysisGraphsContainer';
import { RiskConfigurationModal } from './RiskConfigurationModal';
import { TimeIntervalSwitch } from './TimeIntervalSwitch';
import { ExportPaintRegionAnalyticsButton } from './ExportPaintRegionAnalyticsButton';
import { KpiContainer } from '@/modules/KpiTracker/components/KpiContainer';
import { useInitializeKpiFiltersEffect } from '@/modules/KpiTracker/hooks/useInitializeKpiFiltersEffect';

export const Assets = () => {
  const router = useRouter();
  const { platformId } = router.query;
  const platformName = useAtomValue(state.platformName);
  const isAdmin = useFeatureFlag('admin');
  const isGenericEngineer = useFeatureFlag('generic-engineer');
  const canAccessKpiTracker = useHavePermission(Permissions.READ_KPI_LOGS);

  const [paintBlockTableXlsxProcessor, setPaintBlockTableXlsxProcessor] = useState<
    XlsxProcessor | undefined
  >(undefined);

  const { loading, data } = useAllStructuresForHomePageQuery({
    variables: {
      input: {
        platformId: platformId?.toString(),
      },
    },
  });

  const [activeDeployments, setActiveDeployments] = useState<
    AllStructuresForHomePageQuery['allStructures'] | undefined
  >([]);
  const [previousDeployments, setPreviousDeployments] = useState<
    AllStructuresForHomePageQuery['allStructures'] | undefined
  >([]);
  const [isRiskConfigModalOpen, setIsRiskConfigModalOpen] = useState(false);
  const [selectedDeploymentIds, setSelectedDeploymentIds] = useState<string[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  const [syncAnalyticsData] = useSyncAnalyticsDataMutation();
  const { showSnackBar } = useSnackBarMessage({ atom: snackbarMessage });

  useInitializeKpiFiltersEffect();

  useEffect(() => {
    if (activeDeployments?.length) {
      setSelectedDeploymentIds(activeDeployments.map((deployment) => deployment.id));
    }
  }, [activeDeployments]);

  const handleDeploymentsChange = useCallback((newSelectedIds: string[]) => {
    setSelectedDeploymentIds(newSelectedIds);
  }, []);

  useEffect(() => {
    const { allStructures } = data || {};

    setActiveDeployments(allStructures?.filter((item) => item.isActive === true));
    setPreviousDeployments(allStructures?.filter((item) => item.isActive !== true));
  }, [loading, data]);

  const downloadXlsxFile = useCallback(async () => {
    if (paintBlockTableXlsxProcessor) {
      const fileName = `${platformName} ${new Date().getFullYear()} - Aggregate Ri Values.xlsx`;
      paintBlockTableXlsxProcessor.download(fileName);
    }
  }, [paintBlockTableXlsxProcessor, platformName]);

  const deploymentIds = useMemo(() => {
    return activeDeployments ? activeDeployments?.map((item) => item.id) : [];
  }, [activeDeployments]);

  const handleSyncData = useCallback(async () => {
    if (!platformId || !selectedDeploymentIds.length) return;

    setIsSyncing(true);
    showSnackBar('Syncing data in progress', 'info');
    try {
      const result = await syncAnalyticsData({
        variables: {
          structureIds: selectedDeploymentIds,
          platformId: platformId.toString(),
        },
      });

      if (result.data?.syncAnalyticsData?.success) {
        showSnackBar('Analytics data synced successfully!', 'success');
      } else {
        showSnackBar('Failed to sync analytics data', 'error');
      }
    } catch (error) {
      showSnackBar('An error occurred while syncing analytics data', 'error');
      console.error('Sync error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [platformId, selectedDeploymentIds, syncAnalyticsData, showSnackBar]);

  const tabsData = useMemo(() => {
    const baseTabs = [
      {
        id: 0,
        label: (
          <Stack direction="row" alignItems="center" gap="0.8rem">
            <span>High level insights</span>
            <AbyssIcon iconName="beta" size="md" />
          </Stack>
        ),
        component: (
          <PaintBockAndDeckGraphsContainer
            activeDeploymentIds={deploymentIds}
            selectedDeploymentIds={selectedDeploymentIds}
            setPaintBlockTableXlsxProcessor={setPaintBlockTableXlsxProcessor}
            activeDeployments={activeDeployments || []}
            isAtPlatformsPage={true}
          />
        ),
      },
    ];

    if (!isGenericEngineer) {
      baseTabs.push({
        id: 1,
        label: <span>Temporal analysis</span>,
        component: <TemporalAnalysisGraphsContainer activeDeploymentIds={deploymentIds} />,
      });
    }
    if (canAccessKpiTracker) {
      baseTabs.push({
        id: 2,
        label: (
          <Stack direction="row" alignItems="center" gap="0.8rem">
            <span>Paint & Crew</span>
            <AbyssIcon iconName="beta" size="md" />
          </Stack>
        ),
        component: (
          <Box sx={{ paddingBottom: '4rem' }}>
            <KpiContainer deployments={activeDeployments || []} />
          </Box>
        ),
      });
    }

    return baseTabs;
  }, [
    deploymentIds,
    isGenericEngineer,
    activeDeployments,
    selectedDeploymentIds,
    canAccessKpiTracker,
  ]);

  const [selectedTabId, setSelectedTabId] = useState(tabsData[0].id);

  const handleTabsChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabId(newValue);
  };

  return loading ? (
    <Box sx={{ position: 'relative', top: '25rem' }}>
      <PageLoader />
    </Box>
  ) : (
    <MainContainer sx={{ backgroundColor: abyssColors.primary[50] }}>
      <AssetsContainer>
        <Stack direction="row" alignItems="center" gap={1}>
          <ArrowBackIcon onClick={() => router.back()} style={{ cursor: 'pointer' }} />
          <Typography variant="h5" fontWeight={700} color={abyssColors.primary[700]}>
            {platformName}
          </Typography>
        </Stack>

        <Typography variant="body2" color={abyssColors.primary[400]}>
          Select a deployment for more insights
        </Typography>

        <Typography
          variant="body2"
          fontSize={14}
          fontWeight={500}
          mt={3}
          color={abyssColors.primary[500]}
          mb={2}
        >
          Active Deployments
        </Typography>
        <Grid container rowSpacing={2} columnSpacing={2}>
          {activeDeployments?.map((item, index) => (
            <DeploymentCard
              key={`${item.id}-${index}`}
              deploymentStatus="active"
              structure={item}
            />
          ))}
        </Grid>

        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography
            variant="body2"
            fontWeight={500}
            fontSize={14}
            mt={3}
            color={abyssColors.primary[500]}
            mb={2}
          >
            Previous Deployments
          </Typography>
          {activeDeployments && activeDeployments?.length > 0 && (
            <Button
              onClick={downloadXlsxFile}
              sx={{
                color: abyssColors.primary[500],
                padding: '0.4rem 1rem',
                height: 'auto',
                gap: '1rem',
              }}
            >
              <AbyssIcon iconName="file-download-line" />
              <Typography
                variant="body2"
                fontWeight={500}
                fontSize="1.3rem"
                lineHeight="normal"
                textTransform="none"
              >
                Export Excel
              </Typography>
            </Button>
          )}
        </Stack>
        <Grid container rowSpacing={2} columnSpacing={2}>
          {previousDeployments?.map((item) => (
            <DeploymentCard key={item.id} deploymentStatus="inActive" structure={item} />
          ))}
        </Grid>
        <Box
          sx={{
            marginTop: 5,
            borderBottom: 1,
            borderColor: 'divider',
            '& button': { textTransform: 'none', fontWeight: 400 },
            '& .Mui-selected': { fontWeight: 500 },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Tabs value={selectedTabId} onChange={handleTabsChange}>
              {tabsData.map((tab) => (
                <Tab
                  key={tab.id}
                  label={tab.label}
                  sx={{
                    color: abyssColors.primary[500],
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: abyssColors.primary[700],
                    },
                  }}
                />
              ))}
            </Tabs>
            {selectedTabId === 0 && (
              <Stack direction="row" spacing={1}>
                <TimeIntervalSwitch />
                {isAdmin && (
                  <ExportPaintRegionAnalyticsButton
                    structureIds={deploymentIds}
                    platformName={platformName}
                  />
                )}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <AbyssIcon iconName="gear" style={{ height: '1.6rem', width: '1.6rem' }} />
                  }
                  onClick={() => setIsRiskConfigModalOpen(true)}
                  sx={{
                    color: abyssColors.primary[500],
                    borderColor: abyssColors.primary[300],
                    '&:hover': {
                      borderColor: abyssColors.primary[500],
                      backgroundColor: abyssColors.primary[50],
                    },
                  }}
                >
                  Configure
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={isSyncing || !selectedDeploymentIds.length}
                  onClick={handleSyncData}
                  startIcon={
                    <AbyssIcon iconName="retry" style={{ height: '1.6rem', width: '1.6rem' }} />
                  }
                  sx={{
                    color: abyssColors.primary[500],
                    borderColor: abyssColors.primary[300],
                    '&:hover': {
                      borderColor: abyssColors.primary[500],
                      backgroundColor: abyssColors.primary[50],
                    },
                  }}
                >
                  {isSyncing ? 'Syncing...' : 'Sync Data'}
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>
        {tabsData.map((tab) => (
          <TabContent key={tab.id} selectedTabId={selectedTabId} id={tab.id}>
            {tab.component}
          </TabContent>
        ))}
      </AssetsContainer>
      {isRiskConfigModalOpen && (
        <RiskConfigurationModal
          open={isRiskConfigModalOpen}
          onClose={() => setIsRiskConfigModalOpen(false)}
          activeDeployments={activeDeployments || []}
          onDeploymentsChange={handleDeploymentsChange}
          selectedDeploymentIds={selectedDeploymentIds}
        />
      )}
    </MainContainer>
  );
};
